// ═══════════════════════════════════════════════════════════════
// Perimeter Module — Constants
// 1:1 port from perimeter-embedded.html
// HERE category IDs added for POI scanning via HERE Browse API
// ═══════════════════════════════════════════════════════════════

export const MILES_TO_METERS = 1609.34;
export const R_EARTH = 6371000;
export const R_MILES = 3958.8;

export const RADIUS_PRESETS = [0.25, 0.5, 0.75, 1.0, 1.5, 2.0, 3.0, 5.0];

export interface POICategory {
  key: string;
  label: string;
  icon: string;
  osmTag: string;
  extraOsmTags?: string[];
  placesKeyword?: string;
  hereCategory?: string;
}

export const POI_CATEGORIES: POICategory[] = [
  { key: 'school', label: 'Schools', icon: '\u{1F3EB}', osmTag: 'amenity=school', hereCategory: '800-8200-0174' },
  { key: 'hospital', label: 'Hospitals / Medical', icon: '\u{1F3E5}', osmTag: 'amenity=hospital', hereCategory: '800-8000-0159' },
  { key: 'clinic', label: 'Clinics / Urgent Care', icon: '\u{1FA7A}', osmTag: 'amenity=clinic', hereCategory: '800-8000-0160' },
  { key: 'fire_station', label: 'Fire Stations', icon: '\u{1F692}', osmTag: 'amenity=fire_station', hereCategory: '700-7300-0289' },
  { key: 'police', label: 'Police / Law Enforcement', icon: '\u{1F694}', osmTag: 'amenity=police', hereCategory: '700-7300-0290' },
  { key: 'worship', label: 'Places of Worship', icon: '\u26EA', osmTag: 'amenity=place_of_worship', hereCategory: '800-8600-0191' },
  { key: 'government', label: 'Government Buildings', icon: '\u{1F3DB}\uFE0F', osmTag: 'office=government', hereCategory: '700-7400-0286' },
  { key: 'fuel', label: 'Gas / Fuel Stations', icon: '\u26FD', osmTag: 'amenity=fuel', hereCategory: '700-7600-0116' },
  { key: 'pharmacy', label: 'Pharmacies', icon: '\u{1F48A}', osmTag: 'amenity=pharmacy', hereCategory: '600-6400-0000' },
  { key: 'supermarket', label: 'Supermarkets / Groceries', icon: '\u{1F6D2}', osmTag: 'shop=supermarket', hereCategory: '600-6300-0066' },
  { key: 'childcare', label: 'Childcare / Daycares', icon: '\u{1F476}', osmTag: 'amenity=childcare', extraOsmTags: ['amenity=kindergarten'], placesKeyword: 'daycare childcare preschool', hereCategory: '800-8200-0175' },
  { key: 'nursing', label: 'Nursing Homes / Assisted Living', icon: '\u{1F3E0}', osmTag: 'amenity=nursing_home', extraOsmTags: ['amenity=social_facility'], placesKeyword: 'nursing home assisted living senior care', hereCategory: '800-8000-0163' },
  { key: 'shelter', label: 'Emergency Shelters', icon: '\u{1F3D5}\uFE0F', osmTag: 'amenity=shelter', hereCategory: '550-5520-0357' },
  // Below entries — hereCategory is best-guess for the BROWSE REQUEST only.
  // The matcher in scanPOIs ignores these IDs entirely and uses HERE category
  // names + POI title keywords to bucket results, so even a wrong ID here just
  // means HERE may not return those POIs (the worst case is they're missing,
  // not mis-bucketed).
  { key: 'library', label: 'Libraries', icon: '\u{1F4DA}', osmTag: 'amenity=library', hereCategory: '800-8400-0141' },
  { key: 'community_center', label: 'Community Centers', icon: '\u{1F3D8}\uFE0F', osmTag: 'amenity=community_centre', hereCategory: '550-5510-0203' },
  { key: 'post_office', label: 'Post Offices', icon: '\u{1F4EE}', osmTag: 'amenity=post_office', hereCategory: '800-8300-0173' },
];

export const DENSITY_MAP: Record<string, { low: number; high: number }> = {
  rural: { low: 50, high: 300 },
  suburban: { low: 1000, high: 4000 },
  urban: { low: 5000, high: 15000 },
  dense_urban: { low: 15000, high: 50000 },
};

export const DIR_ORDER = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

export const DIR_LABELS: Record<string, string> = {
  N: 'North', NE: 'Northeast', E: 'East', SE: 'Southeast',
  S: 'South', SW: 'Southwest', W: 'West', NW: 'Northwest',
};

export const PRIORITY_LABELS: Record<number, string> = { 1: 'Highway', 2: 'Primary', 3: 'Secondary', 4: 'Local' };
export const PRIORITY_COLORS: Record<number, string> = { 1: '#E63946', 2: '#F4A261', 3: '#3F72AF', 4: '#5A7BA6' };

export type PerimeterShape = 'circle' | 'square';
export type DensityType = 'rural' | 'suburban' | 'urban' | 'dense_urban';

export interface IncidentTemplate {
  name: string;
  icon: string;
  radius: number;
  shape: PerimeterShape;
  density: DensityType;
}

export const INCIDENT_TEMPLATES: IncidentTemplate[] = [
  { name: 'Structure Fire', icon: 'fa-fire', radius: 0.25, shape: 'square', density: 'urban' },
  { name: 'Wildfire', icon: 'fa-fire-flame-curved', radius: 2.0, shape: 'circle', density: 'rural' },
  { name: 'HazMat Spill', icon: 'fa-biohazard', radius: 0.5, shape: 'circle', density: 'suburban' },
  { name: 'Gas Leak', icon: 'fa-smog', radius: 0.25, shape: 'circle', density: 'urban' },
  { name: 'Active Shooter', icon: 'fa-crosshairs', radius: 0.15, shape: 'circle', density: 'dense_urban' },
  { name: 'Flood Event', icon: 'fa-water', radius: 1.0, shape: 'square', density: 'suburban' },
  { name: 'Tornado', icon: 'fa-tornado', radius: 0.5, shape: 'square', density: 'suburban' },
  { name: 'Large Event', icon: 'fa-people-group', radius: 0.5, shape: 'square', density: 'dense_urban' },
  { name: 'Train Derail', icon: 'fa-train', radius: 1.0, shape: 'circle', density: 'suburban' },
  { name: 'Plane Crash', icon: 'fa-plane-slash', radius: 0.5, shape: 'circle', density: 'rural' },
];
